import { useT } from '@dia/i18n';
import { cn } from './cn';

export type LangToggleProps = {
  className?: string;
  /** Kept for callers that still pass it. Both variants now show the full word. */
  variant?: 'switch' | 'labeled';
};

/**
 * Each language is always called by its own name, in that language, whatever
 * the screen is currently set to: a person who cannot read the page must still
 * be able to find the button for the language they can. Never the two-letter
 * codes, which mean nothing to a crew member who has not seen them before.
 */
export const LANGUAGE_NAMES = { es: 'Español', en: 'English' } as const;

/**
 * The Spanish and English switch that sits at the top of both shells.
 * Every string is already in both languages, so flipping costs nothing at
 * runtime and works with no connection.
 */
export function LangToggle({ className }: LangToggleProps) {
  const { lang, setLang, t } = useT();

  return (
    <div
      role="group"
      aria-label={t('lang.toggle_label')}
      // The full words are wider than the old two letters. At 200 percent zoom (a 195 px
      // viewport) the two buttons stack instead of taking the page sideways (defect R2).
      className={cn('inline-flex max-w-full flex-wrap rounded border border-line overflow-hidden', className)}
    >
      {(['es', 'en'] as const).map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            aria-pressed={active}
            lang={code}
            className={cn(
              'min-h-tap whitespace-nowrap px-3 text-caption font-medium',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
              active ? 'bg-accent text-onAccent' : 'bg-surface text-inkMuted',
            )}
          >
            {LANGUAGE_NAMES[code]}
          </button>
        );
      })}
    </div>
  );
}
